import { FormControl, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import SidebarPendingOrderDataView from './SidebarPendingOrderDataView'
import SidebarInitActionsSlTp from './SidebarPendingOrderSlTp'
import {handleCancelPending, updateSlTp} from '../../../../../helpers'
import { useDefaultComponentTools } from '../../../../../hooks/useDefaultComponentTools'
import {selectAssetTailDigits, selectPipDecimalPlaces} from '../../../../../store/redusers/assets/selectors'
import { selectAccessToken } from '../../../../../store/redusers/auth/selectors'
import {
	setClosePendingAction,
	setLotAction,
	setUpdatePendingOrderAction
} from '../../../../../store/redusers/orders/actions'
import {
	selectExecutionEntryRate,
	selectLotCountData,
	selectPipValue,
	selectStopLoss,
	selectTakeProfit
} from '../../../../../store/redusers/orders/selectors'
import {
	selectPositionDefaultItem,
	selectTailDigitOfCurrency
} from '../../../../../store/redusers/positions/selectors'
import { selectMaxLotCount, selectMinLotCount } from '../../../../../store/redusers/user/selectors'
import AppButton from '../../../../AppButton'
import AppNumberField from '../../../../AppNumberField'
import AppPopup from '../../../../AppPopup'
import AppTooltip from '../../../../AppTooltip'
import HighChart from '../Highchart/Highchart'

function SidebarUpdatePendingOrder({ toUpdatePendingOrder }) {
	const {dispatch, alert, theme, t} = useDefaultComponentTools(true)
	const accessToken = useSelector(selectAccessToken)
	const stopLoss = useSelector(selectStopLoss)
	const lotCountData = useSelector(selectLotCountData)
	const takeProfit = useSelector(selectTakeProfit)
	const pipValue = useSelector(selectPipValue)
	const myTrade = useSelector(selectPositionDefaultItem('pending', toUpdatePendingOrder))
	const tailDigitOfCurrency = useSelector(selectTailDigitOfCurrency)
	const [isShowPopup, setIsShowPopup] = useState(false)
	const myTradeAssetId = myTrade.assetId
	const myTradeAssetClass = myTrade.assetClass
	const asset = useMemo(() => {
		return {'assetSoId': 'asset_'+myTradeAssetId, 'assetType': myTradeAssetClass}
	}, [myTradeAssetId, myTradeAssetClass])
	const executionEntryRate = useSelector(selectExecutionEntryRate)
	const pipDecimalPlaces = useSelector(selectPipDecimalPlaces(asset.assetType, asset.assetSoId))
	const tailDigits = useSelector(selectAssetTailDigits(asset.assetType, asset.assetSoId))
	const [takeProfitSend, setTakeProfitSend] = useState(
		isNaN(parseFloat(myTrade.takeProfit)) ? -1 : parseFloat(myTrade.takeProfit)
	)
	const [stopLossSend, setStopLossSend] = useState(
		isNaN(parseFloat(myTrade.stopLoss)) ? -1 : parseFloat(myTrade.stopLoss)
	)
	const [takeProfitAmount, setTakeProfitAmount] = useState(0)
	const [stopLossAmount, setStopLossAmount] = useState(0)
	let stopLossUpdate = (myTrade.stopLoss === '' || +myTrade.stopLoss === 0 || +myTrade.stopLoss === 10000000 ?
		'' : myTrade.stopLoss)
	let takeProfitUpdate = (myTrade.takeProfit === '' || +myTrade.takeProfit === 0 || +myTrade.takeProfit === 10000000 ?
		'' : myTrade.takeProfit)
	const minLotCount = useSelector(selectMinLotCount)
	const maxLotCount = useSelector(selectMaxLotCount)
	useEffect(() => {
		dispatch(setLotAction({ [myTrade.assetId]: +myTrade.leverage }))
		if (stopLossUpdate === '' || takeProfitUpdate === '') {
			if(myTrade.position === 'sell') {
				setStopLossSend(stopLossUpdate === '' || stopLossUpdate ? 10000000 : stopLossUpdate)
				setTakeProfitSend(takeProfitUpdate === '' ? 0 : takeProfitUpdate)
			} else {
				setStopLossSend(stopLossUpdate === '' ? 0 : stopLossUpdate)
				setTakeProfitSend(takeProfitUpdate === '' ? 10000000 : takeProfitUpdate)
			}
		}
	}, [myTrade.assetId])

	function updateSlTpUpdate(type, value) {
		const execute = executionEntryRate || myTrade.executionEntryRate
		updateSlTp(
			+execute,
			+pipValue,
			pipDecimalPlaces,
			myTrade.position,
			type,
			+value,
			tailDigitOfCurrency,
			setStopLossAmount,
			setTakeProfitAmount,
			tailDigits)
	}

	useEffect(() => {
		if (takeProfit) updateSlTpUpdate('tp', takeProfit)
		if (stopLoss) updateSlTpUpdate('sl', stopLoss)
	},[executionEntryRate, pipValue])

	const onSetLots = (fieldName, fieldValue) => {
		dispatch(setLotAction({ [myTrade.assetId]: fieldValue }))
	}

	const pendingOrderData = `${
		'&assetId=' + myTradeAssetId
		+'&executionEntryRate=' + executionEntryRate
		+'&leverage=' + lotCountData[myTrade.assetId]
		+'&stopLoss=' + stopLossSend
		+'&stopLossAmount=' + stopLossAmount
		+'&takeProfit=' + takeProfitSend
		+'&takeProfitAmount=' + takeProfitAmount
		+'&type=' + myTrade.orderType
		+'&userId=' + myTrade.userId
		+'&tradeId=' + toUpdatePendingOrder
	}`

	const handleUpdateOpenTrade = async (e) => {
		e.preventDefault()
		try {
			alert.info(t('Executing order')+'...', {
				loader: true,
				timeout: 0,
				title: 'Execute order'
			})
			let res = await dispatch(setUpdatePendingOrderAction(
				pendingOrderData,
				accessToken,
				myTrade.userId,
				alert
			))
			if (res) {
				alert.removeAll()
				alert.success(t('Update trade data'))
			}
		}
		catch (e) {
			alert.removeAll()
			'message' in e && alert.error(e.message)
		}
	}

	useEffect(() => {
		if (!myTrade) {
			return
		}
		const tempTakeProfitAmount = (+myTrade.takeProfit === 10000000 || +myTrade.takeProfit === 0) ?
			'' : parseFloat(myTrade.takeProfitAmount)
		const tempStopLossAmount = (+myTrade.stopLoss === 0 || myTrade.stopLoss === 10000000 ) ?
			'' : parseFloat(myTrade.stopLossAmount)
		const tempSendTPValue = isNaN(parseFloat(myTrade.takeProfit)) ? -1 : parseFloat(myTrade.takeProfit)
		const tempSendSLValue = isNaN(parseFloat(myTrade.stopLoss)) ? -1 : parseFloat(myTrade.stopLoss)
		if (!myTrade.takeProfitAmount) {
			// updateSlTpUpdate('tp', tempSendTPValue)
		}
		if (!myTrade.stopLossAmount) {
			setTimeout(() => {
				// updateSlTpUpdate('sl', tempSendSLValue)
			}, 800)
		}
		myTrade.takeProfitAmount && tempTakeProfitAmount && setTakeProfitAmount(tempTakeProfitAmount)
		myTrade.stopLossAmount && tempStopLossAmount && setStopLossAmount(tempStopLossAmount)
		tempSendSLValue && setStopLossSend(tempSendSLValue)
		tempSendTPValue && setTakeProfitSend(tempSendTPValue)
	}, [myTrade.id])

	return (
		<form onSubmit={() => handleUpdateOpenTrade}>
			<FormControl fullWidth>
				<Box sx={{ display: 'flex' }}>
					<Typography sx={{ mb: 1, mr: 0.5 }}>
						{t('Lots')}
					</Typography>
					<AppTooltip
						sxElement={{position: 'absolute', top: '-5px'}}
						title={`Lot size is ${minLotCount + ' - ' + maxLotCount}`} icon='info' placement='top-start'/>
				</Box>
				<AppNumberField
					value={+lotCountData[myTrade.assetId] || +myTrade.leverage}
					minLotCount={+minLotCount}
					maxLotCount={+maxLotCount}
					onGetValue={onSetLots}
					name='leverage'
				/>
			</FormControl>
			<SidebarPendingOrderDataView
				lots={lotCountData[myTrade.assetId]  || myTrade.leverage}
				asset={asset}
				orderId={toUpdatePendingOrder}
			/>
			<SidebarInitActionsSlTp
				setTakeProfitSend={setTakeProfitSend}
				setStopLossSend={setStopLossSend}
				takeProfitUpdate={takeProfitUpdate}
				stopLossUpdate={stopLossUpdate}
				stopLossAmount={stopLossAmount}
				takeProfitAmount={takeProfitAmount}
				isOpenTradeTab={false}
				updateSlTp={updateSlTpUpdate}
				assetId={myTrade.assetId}
				myId={myTrade.id}
				lastPrice={myTrade.executionEntryRate}
				pendingPosition={true}
			/>
			<Box sx={{mb:'24px'}}><HighChart
				pendingAskBid={myTrade?.position}
				oldUserRate={+myTrade?.executionEntryRate}
				asset={asset}/>
			</Box>
			<AppButton
				onClick={() => setIsShowPopup(true)}
				title={'Close Trade'}
				inputSx={{
					':hover': {background: theme.palette.red, opacity: 0.7},
					background: theme.palette.red,
					mb: '20px',
					textTransform: 'capitalize'
				}}
			/>
			<AppButton
				onClick={handleUpdateOpenTrade}
				title={'Update'}
				inputSx={{ textTransform: 'capitalize'}} />
			{!isShowPopup ?
				null :
				<AppPopup
					onAction={() => handleCancelPending(
						dispatch,
						setClosePendingAction(
							{'tradeId': myTrade.id},
							accessToken,
							myTrade.userId),
						alert, t
					)}
					setOpenedComponent={setIsShowPopup}
					title={'Close Trade'}
					text={'Are you sure. You want to close trade?'}
					actionTitle={'Close'}
				/>
			}
		</form>
	)
}

SidebarUpdatePendingOrder.propTypes = {
	toUpdatePendingOrder: PropTypes.string
}

export default SidebarUpdatePendingOrder
